/**
 * Trade-In Catalog API Composable
 * Fetches brands, categories, products, SKUs, inspection properties, and calculates quotes
 */
import { apiGet, apiPost } from '~/utils/api'

// ==================== Types ====================

export interface Brand {
  id: string
  code: string
  name: string
  logoUrl: string | null
  sortOrder: number
}

export interface Category {
  id: string
  code: string
  name: string
  icon: string | null
  brandId: string
  brandCode: string
  sortOrder: number
  nameTranslations: Record<string, string> | null
}

export interface Product {
  id: string
  name: string
  modelCode: string | null
  imageUrl: string | null
  maxPrice: number
  minPrice: number
  categoryId: string
  categoryCode: string
  isEnvironmentalOnly: boolean
  sortOrder: number
}

export interface ProductSku {
  id: string
  productId: string
  skuPpvIds: string[]
  basePrice: number
}

export interface PropertyValue {
  id: string
  code: string
  name: string
  description: string | null
  nameTranslations: Record<string, string> | null
  weight: number
  priceAdjustment: number
  isRejection: boolean
  isEnvironmental: boolean
  isNoAdditional: boolean
  noAdditionalTips: string | null
  presetPpvIds: string[]
  exclusionPpvIds: string[]
  tags: string[]
  icon: string | null
  sortOrder: number
}

export interface InspectionProperty {
  id: string
  code: string
  name: string
  description: string | null
  nameTranslations: Record<string, string> | null
  descriptionTranslations: Record<string, string> | null
  propertyType: string
  isSkuProperty: boolean
  isRequired: boolean
  stepNumber: number
  sortOrder: number
  displayIcon: string | null
  showType: number | null
  values: PropertyValue[]
}

export interface QuoteDeductionDetail {
  propertyValueId: string
  propertyName: string
  propertyNameTranslations?: Record<string, string> | null
  valueName: string
  valueNameTranslations?: Record<string, string> | null
  amount: number
}

export interface QuoteResult {
  productId: string
  productName: string
  basePrice: number
  finalPrice: number
  totalDeductions: number
  deductions: QuoteDeductionDetail[]
  isEnvironmentalOnly: boolean
  isRejected: boolean
  isPricingPending: boolean
  rejectionReason: string | null
}

// ==================== Composable ====================

export const useTradeInAPI = () => {
  const activeRequests = ref(0)
  const loading = computed(() => activeRequests.value > 0)
  const error = ref<string | null>(null)

  async function fetchBrands(): Promise<Brand[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Brand[]>('/api/app/v1/trade-in/brands')
    } catch (e: any) {
      error.value = e.message || 'Failed to load brands'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchCategories(brandCode: string): Promise<Category[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Category[]>(`/api/app/v1/trade-in/categories?brandCode=${encodeURIComponent(brandCode)}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load categories'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchCategory(categoryCode: string): Promise<Category> {
    activeRequests.value++
    try { return await apiGet<Category>(`/api/app/v1/trade-in/categories/${encodeURIComponent(categoryCode)}`) }
    finally { activeRequests.value-- }
  }

  async function fetchAllCategories(): Promise<Category[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Category[]>('/api/app/v1/trade-in/categories')
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load categories'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchProducts(categoryCode: string): Promise<Product[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Product[]>(`/api/app/v1/trade-in/products?categoryCode=${encodeURIComponent(categoryCode)}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load products'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchProductsByBrand(brandCode: string): Promise<Product[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Product[]>(`/api/app/v1/trade-in/products?brandCode=${encodeURIComponent(brandCode)}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load products'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function searchProducts(keyword: string): Promise<Product[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Product[]>(`/api/app/v1/trade-in/products/search?q=${encodeURIComponent(keyword)}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to search products'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchProduct(productId: string): Promise<Product> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<Product>(`/api/app/v1/trade-in/products/${productId}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load product'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchProductSkus(productId: string): Promise<ProductSku[]> {
    activeRequests.value++
    error.value = null
    try {
      return await apiGet<ProductSku[]>(`/api/app/v1/trade-in/products/${productId}/skus`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load SKUs'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function fetchProperties(categoryCode: string, step?: number, productId?: string): Promise<InspectionProperty[]> {
    activeRequests.value++
    error.value = null
    try {
      let url = `/api/app/v1/trade-in/properties?categoryCode=${encodeURIComponent(categoryCode)}`
      if (step !== undefined) {
        url += `&step=${step}`
      }
      if (productId) {
        url += `&productId=${encodeURIComponent(productId)}`
      }
      return await apiGet<InspectionProperty[]>(url)
    } catch (e: any) {
      error.value = e.message || 'Failed to load properties'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  async function calculateQuote(productId: string, selectedPropertyValueIds: string[]): Promise<QuoteResult> {
    activeRequests.value++
    error.value = null
    try {
      return await apiPost<QuoteResult>('/api/app/v1/trade-in/quote', {
        productId,
        selectedPropertyValueIds,
      })
    } catch (e: any) {
      error.value = e.message || 'Failed to calculate quote'
      throw e
    } finally {
      activeRequests.value--
    }
  }

  return {
    loading,
    error,
    fetchBrands,
    fetchCategories,
    fetchAllCategories,
    fetchCategory,
    fetchProducts,
    fetchProductsByBrand,
    searchProducts,
    fetchProduct,
    fetchProductSkus,
    fetchProperties,
    calculateQuote,
  }
}
